/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import { vendorCRMService } from '../../../services/VendorCRMService';
import AIService from '../../../services/AIService';
import {
  XIcon, StarIcon, BanIcon, PlusIcon, TrashIcon,
  MailIcon, PhoneIcon, CalendarIcon, TagIcon, SparklesIcon
} from '@heroicons/react/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/solid';

const SUGGESTED_TAGS = ['reliable', 'vip', 'returning', 'food', 'new', 'top seller', 'avoid'];

// Inline modal for AI-drafted invite email
const InviteDraftModal = ({ draft, onClose, vendorEmail }) => {
  const { t } = useTranslation();
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);

  const handleCopy = () => {
    navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`);
  };

  const handleOpenInMail = () => {
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body);
    window.open(`mailto:${vendorEmail || ''}?subject=${encodedSubject}&body=${encodedBody}`);
  };

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col">
          {/* Modal header */}
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                <SparklesIcon className="w-3 h-3" />
                {t('vendorCRM.aiDraft')}
              </span>
              <h3 className="font-semibold text-gray-900 text-sm">{t('vendorCRM.inviteEmail')}</h3>
            </div>
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition">
              <XIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Editable fields */}
          <div className="px-5 py-4 space-y-3">
            <div>
              <label className="block text-[10px] text-gray-400 uppercase tracking-wide mb-1">{t('common.subject')}</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
              />
            </div>
            <div>
              <label className="block text-[10px] text-gray-400 uppercase tracking-wide mb-1">{t('common.body')}</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={10}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-400 resize-none leading-relaxed"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 py-3.5 border-t border-gray-100 flex items-center gap-2 justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
            >
              {t('common.close')}
            </button>
            <button
              onClick={handleCopy}
              className="px-4 py-2 text-sm border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              {t('common.copy')}
            </button>
            <button
              onClick={handleOpenInMail}
              className="flex items-center gap-1.5 px-4 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition"
            >
              <MailIcon className="w-3.5 h-3.5" />
              {t('vendorCRM.openInMail')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const VendorDetail = ({ vendor, allVendors = [], onClose, onUpdate }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [vendorData, setVendorData] = useState(vendor);
  const [notes, setNotes] = useState([]);
  const [eventHistory, setEventHistory] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [newTag, setNewTag] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [activeTab, setActiveTab] = useState('info');

  // AI invite state
  const [inviteDraft, setInviteDraft] = useState(null);
  const [loadingInvite, setLoadingInvite] = useState(false);

  // AI note summary state
  const [aiSummary, setAiSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!user?.uid || !vendor?.id) return;
      setLoading(true);
      try {
        const result = await vendorCRMService.getVendorDetails(user.uid, vendor.id);
        if (result.success) {
          setVendorData(result.vendor);
          setNotes(result.notes);
          setEventHistory(result.eventHistory);

          // Trigger AI summary if there are enough notes
          if (result.notes.length >= 2) {
            setLoadingSummary(true);
            try {
              const summary = await AIService.summarizeNotes(result.notes, result.vendor);
              setAiSummary(summary);
            } catch (err) {
              console.error('Error generating AI summary:', err);
            } finally {
              setLoadingSummary(false);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching vendor details:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [user?.uid, vendor?.id]);

  const handleDraftInvite = async () => {
    setLoadingInvite(true);
    try {
      const draft = await AIService.generateVendorInvite(vendorData, eventHistory);
      setInviteDraft(draft);
    } catch (err) {
      console.error('Error generating invite draft:', err);
    } finally {
      setLoadingInvite(false);
    }
  };

  const handleToggleFavorite = async () => {
    if (!user?.uid) return;
    const result = await vendorCRMService.toggleFavorite(user.uid, vendor.id, !vendorData.isFavorite);
    if (result.success) {
      setVendorData(prev => ({ ...prev, isFavorite: !prev.isFavorite }));
      onUpdate?.();
    }
  };

  const handleToggleBlocked = async () => {
    if (!user?.uid) return;
    const result = await vendorCRMService.toggleBlocked(user.uid, vendor.id, !vendorData.isBlocked);
    if (result.success) {
      setVendorData(prev => ({
        ...prev,
        isBlocked: !prev.isBlocked,
        status: !prev.isBlocked ? 'blocked' : 'active'
      }));
      onUpdate?.();
    }
  };

  const handleAddNote = async () => {
    if (!user?.uid || !newNote.trim()) return;
    setAddingNote(true);
    try {
      const result = await vendorCRMService.addNote(user.uid, vendor.id, newNote.trim());
      if (result.success) {
        setNotes(prev => [{ id: result.noteId, content: newNote.trim(), createdAt: new Date() }, ...prev]);
        setNewNote('');
      }
    } catch (error) {
      console.error('Error adding note:', error);
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!user?.uid) return;
    const result = await vendorCRMService.deleteNote(user.uid, vendor.id, noteId);
    if (result.success) setNotes(prev => prev.filter(n => n.id !== noteId));
  };

  const handleAddTag = async (tag) => {
    const t = (tag || newTag).trim();
    if (!user?.uid || !t) return;
    if (vendorData.tags?.includes(t)) { setNewTag(''); return; }
    const result = await vendorCRMService.addTag(user.uid, vendor.id, t);
    if (result.success) {
      setVendorData(prev => ({ ...prev, tags: [...(prev.tags || []), t] }));
      setNewTag('');
      onUpdate?.();
    }
  };

  const handleRemoveTag = async (tag) => {
    if (!user?.uid) return;
    const result = await vendorCRMService.removeTag(user.uid, vendor.id, tag);
    if (result.success) {
      setVendorData(prev => ({ ...prev, tags: (prev.tags || []).filter(t => t !== tag) }));
      onUpdate?.();
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const unusedSuggestions = SUGGESTED_TAGS.filter(t => !(vendorData.tags || []).includes(t));

  const tabs = [
    { id: 'info', label: t('common.info') },
    { id: 'notes', label: t('common.notes'), count: notes.length },
    { id: 'history', label: t('vendorCRM.history'), count: eventHistory.length },
  ];

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="fixed inset-0 bg-black/40" onClick={onClose} />

          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col">

            {/* Header */}
            <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                {vendorData.companyLogoURL ? (
                  <img src={vendorData.companyLogoURL} alt={vendorData.businessName}
                    className="w-11 h-11 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="w-11 h-11 bg-gray-100 rounded-full flex items-center justify-center shrink-0">
                    <span className="text-gray-500 font-semibold text-base">
                      {vendorData.businessName?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  </div>
                )}
                <div>
                  <h2 className="font-semibold text-gray-900 text-base leading-tight">{vendorData.businessName}</h2>
                  <p className="text-xs text-gray-400">{vendorData.businessType || 'Vendor'}</p>
                </div>
              </div>

              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={handleToggleFavorite}
                  className={`p-2 rounded-lg transition ${
                    vendorData.isFavorite ? 'text-yellow-400 bg-yellow-50' : 'text-gray-300 hover:text-gray-500 hover:bg-gray-100'
                  }`}
                  title={vendorData.isFavorite ? t('vendorCRM.removeFavorite') : t('vendorCRM.markFavoriteLabel')}
                >
                  {vendorData.isFavorite ? <StarSolidIcon className="w-5 h-5" /> : <StarIcon className="w-5 h-5" />}
                </button>
                <button
                  onClick={handleToggleBlocked}
                  className={`p-2 rounded-lg transition ${
                    vendorData.isBlocked ? 'text-red-400 bg-red-50' : 'text-gray-300 hover:text-red-400 hover:bg-gray-100'
                  }`}
                  title={vendorData.isBlocked ? t('vendorCRM.unblock') : t('vendorCRM.blockVendor')}
                >
                  <BanIcon className="w-5 h-5" />
                </button>
                <button onClick={onClose} className="p-2 text-gray-300 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition">
                  <XIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* AI Draft Invite CTA — only show if not blocked */}
            {!vendorData.isBlocked && vendorData.businessEmail && (
              <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between gap-3">
                <p className="text-xs text-gray-500">
                  {vendorData.eventsAttended > 0
                    ? t('vendorCRM.attendedEvents', { count: vendorData.eventsAttended })
                    : t('vendorCRM.readyToInvite')}
                </p>
                <button
                  onClick={handleDraftInvite}
                  disabled={loadingInvite}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white text-xs font-medium rounded-lg hover:bg-gray-800 disabled:opacity-60 transition shrink-0"
                >
                  {loadingInvite ? (
                    <>
                      <span className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                      {t('vendorCRM.drafting')}
                    </>
                  ) : (
                    <>
                      <SparklesIcon className="w-3.5 h-3.5" />
                      {t('vendorCRM.draftInviteAI')}
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Blocked notice */}
            {vendorData.isBlocked && (
              <div className="px-5 py-2.5 bg-red-50 border-b border-red-100 flex items-center justify-between">
                <p className="text-xs text-red-600 font-medium">{t('vendorCRM.vendorBlocked')}</p>
                <button onClick={handleToggleBlocked} className="text-xs text-red-500 hover:text-red-700 underline">{t('vendorCRM.unblock')}</button>
              </div>
            )}

            {/* Tabs */}
            <div className="flex border-b border-gray-100 px-5">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-2.5 text-sm font-medium border-b-2 transition ${
                    activeTab === tab.id
                      ? 'border-gray-900 text-gray-900'
                      : 'border-transparent text-gray-400 hover:text-gray-600'
                  }`}
                >
                  {tab.label}
                  {tab.count !== undefined && tab.count > 0 && (
                    <span className={`ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full ${
                      activeTab === tab.id ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 rounded-full border-2 border-gray-200 border-t-gray-900 animate-spin" />
                </div>
              ) : (
                <>
                  {/* INFO TAB */}
                  {activeTab === 'info' && (
                    <div className="space-y-5">
                      {/* AI summary card */}
                      {aiSummary && (
                        <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg border border-gray-100">
                          <SparklesIcon className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                          <p className="text-sm text-gray-600 italic">{aiSummary}</p>
                        </div>
                      )}

                      {/* Contact info */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">{t('auth.login.emailLabel')}</p>
                          <p className="text-sm text-gray-800 truncate">{vendorData.businessEmail || '—'}</p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">{t('common.phone')}</p>
                          <p className="text-sm text-gray-800">{vendorData.businessPhone || '—'}</p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">{t('vendorCRM.eventsAttended')}</p>
                          <p className="text-sm font-semibold text-gray-900">{vendorData.eventsAttended || 0}</p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">{t('vendorCRM.totalRevenue')}</p>
                          <p className="text-sm font-semibold text-gray-900">
                            ${((vendorData.totalRevenue || 0) / 100).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      {vendorData.businessDescription && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('common.about')}</p>
                          <p className="text-sm text-gray-600 leading-relaxed">{vendorData.businessDescription}</p>
                        </div>
                      )}

                      {/* Tags */}
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t('vendorCRM.tags')}</p>

                        {/* Current tags */}
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {(vendorData.tags || []).map(tag => (
                            <span key={tag}
                              className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                              {tag}
                              <button onClick={() => handleRemoveTag(tag)} className="text-gray-400 hover:text-gray-700 ml-0.5">
                                <XIcon className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                          {(vendorData.tags || []).length === 0 && (
                            <span className="text-xs text-gray-400">{t('vendorCRM.noTagsYet')}</span>
                          )}
                        </div>

                        {/* Suggested quick-add tags */}
                        {unusedSuggestions.length > 0 && (
                          <div className="mb-2">
                            <p className="text-[10px] text-gray-400 mb-1.5">{t('vendorCRM.quickAdd')}</p>
                            <div className="flex flex-wrap gap-1.5">
                              {unusedSuggestions.map(tag => (
                                <button
                                  key={tag}
                                  onClick={() => handleAddTag(tag)}
                                  className="px-2.5 py-1 text-xs border border-dashed border-gray-300 text-gray-500 rounded-full hover:border-gray-500 hover:text-gray-700 transition"
                                >
                                  + {tag}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Custom tag input */}
                        <div className="flex gap-2 mt-2">
                          <input
                            type="text"
                            value={newTag}
                            onChange={e => setNewTag(e.target.value)}
                            placeholder={t('vendorCRM.customTag')}
                            className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                            onKeyDown={e => e.key === 'Enter' && handleAddTag()}
                          />
                          <button
                            onClick={() => handleAddTag()}
                            disabled={!newTag.trim()}
                            className="px-3 py-1.5 bg-gray-900 text-white rounded-lg text-sm hover:bg-gray-800 disabled:opacity-30 transition"
                          >
                            {t('common.add')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* NOTES TAB */}
                  {activeTab === 'notes' && (
                    <div className="space-y-4">
                      <div className="flex flex-col gap-2">
                        <textarea
                          value={newNote}
                          onChange={e => setNewNote(e.target.value)}
                          placeholder={t('vendorCRM.notePlaceholder')}
                          rows={3}
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-400 resize-none"
                        />
                        <button
                          onClick={handleAddNote}
                          disabled={!newNote.trim() || addingNote}
                          className="self-end px-4 py-2 bg-gray-900 text-white rounded-lg text-sm hover:bg-gray-800 disabled:opacity-30 transition"
                        >
                          {addingNote ? t('common.saving') : t('vendorCRM.addNote')}
                        </button>
                      </div>

                      {notes.length === 0 ? (
                        <p className="text-center text-sm text-gray-400 py-8">{t('vendorCRM.noNotesYet')}</p>
                      ) : (
                        <div className="space-y-2">
                          {notes.map(note => (
                            <div key={note.id} className="p-3.5 bg-gray-50 rounded-lg group">
                              <div className="flex justify-between items-start gap-2">
                                <p className="text-sm text-gray-700 leading-relaxed">{note.content}</p>
                                <button
                                  onClick={() => handleDeleteNote(note.id)}
                                  className="opacity-0 group-hover:opacity-100 p-1 text-gray-300 hover:text-red-400 transition shrink-0"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              </div>
                              <p className="text-[10px] text-gray-400 mt-2">{formatDate(note.createdAt)}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* HISTORY TAB */}
                  {activeTab === 'history' && (
                    <div className="space-y-2">
                      {eventHistory.length === 0 ? (
                        <p className="text-center text-sm text-gray-400 py-8">{t('vendorCRM.noEventHistory')}</p>
                      ) : (
                        eventHistory.map(event => (
                          <div key={event.id} className="p-4 bg-gray-50 rounded-lg">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-gray-900 text-sm">{event.eventName}</p>
                                <p className="text-xs text-gray-400 mt-0.5">{formatDate(event.eventDate)}</p>
                              </div>
                              <div className="text-right shrink-0">
                                <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                  event.status === 'Registered' || event.status === 'Paid'
                                    ? 'bg-green-100 text-green-700'
                                    : event.status === 'Denied'
                                    ? 'bg-red-100 text-red-600'
                                    : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {event.status}
                                </span>
                                {event.revenue > 0 && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    ${(event.revenue / 100).toLocaleString()} {t('vendorCRM.revenue')}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* AI Invite Draft Modal */}
      {inviteDraft && (
        <InviteDraftModal
          draft={inviteDraft}
          vendorEmail={vendorData.businessEmail}
          onClose={() => setInviteDraft(null)}
        />
      )}
    </>
  );
};

export default VendorDetail;
